import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Eyebrow } from "@/components/Reveal";

const ABOUT_IMG = "/about-kitchen.jpg";
const SECONDARY_IMG = "/about-hallway.webp";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="py-24 md:py-32 bg-[#121212] overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5 relative">
          <Reveal>
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2px]">
              <motion.img
                style={{ y, scale: 1.15 }}
                src={ABOUT_IMG}
                alt="Kitchen refurbished by Preece Refurbishments"
                loading="lazy"
                className="w-full h-full object-cover img-treated"
              />
            </div>
          </Reveal>
          <Reveal delay={0.2} className="hidden md:block absolute -bottom-10 -right-6 lg:-right-16 w-56 xl:w-64">
            <div className="aspect-square overflow-hidden rounded-[2px] border-4 border-[#121212] shadow-[0_0_40px_rgba(0,0,0,0.5)]">
              <img
                src={SECONDARY_IMG}
                alt="Refurbished hallway and staircase by Preece Refurbishments"
                loading="lazy"
                className="w-full h-full object-cover img-treated"
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <Eyebrow className="mb-4">The family behind the work</Eyebrow>
            <h2 className="font-serif text-4xl md:text-6xl text-[#f5f5f0] leading-tight mb-8">
              Family owned.
              <br />
              <em className="italic text-[#c5832b]">Craft obsessed.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#a3a3a3] text-base md:text-lg leading-relaxed mb-6">
              Preece Refurbishments is a family business, and that shows in how
              we work. The people who quote your job are the people who turn up
              on site — we never hand your home off to whoever answers the
              phone.
            </p>
            <p className="text-[#a3a3a3] text-base md:text-lg leading-relaxed mb-10">
              We keep crews small, tidy sites daily, and treat every home like
              it belongs to family. That is why most of our work comes from
              recommendations — a neighbour's kitchen, a friend's bathroom, a
              landlord's whole portfolio.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-[2px] overflow-hidden">
              {[
                { k: "Family run", v: "Owner on every site" },
                { k: "One team", v: "Every trade covered" },
                { k: "Free quotes", v: "No obligation, ever" },
              ].map((s) => (
                <div key={s.k} className="bg-[#1a1a1a] p-6">
                  <p className="font-serif text-xl text-[#f5f5f0]">{s.k}</p>
                  <p className="mt-1 text-sm text-[#a3a3a3]">{s.v}</p>
                </div>
              ))}
            </div>
            <p data-testid="about-clients" className="mt-8 text-[#a3a3a3] text-base md:text-lg leading-relaxed">
              {"Today we carry out work for "}
              <span className="text-[#f5f5f0] font-medium">{"homeowners"}</span>
              {", "}
              <span className="text-[#f5f5f0] font-medium">{"tenants"}</span>
              {" and "}
              <span className="text-[#f5f5f0] font-medium">{"multi-home landlords"}</span>
              {", as well as "}
              <span className="text-[#f5f5f0] font-medium">{"local and national estate agents"}</span>
              {" — and we also deal with "}
              <span className="text-[#f5f5f0] font-medium">{"contracted maintenance for landlords"}</span>
              {"."}
            </p>
            <p className="mt-8 font-serif italic text-2xl text-[#d9953c]">
              — The Preece family
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
